/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import {
    IPriceComponentResources,
    ISwatchItem,
    ProductComponentSwatchComponent,
    //RatingComponent,
    AddToCartComponent
} from '@msdyn365-commerce/components';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import {
    // getCatalogId,
    IAny,
    IComponent,
    IComponentProps,
    ICoreContext,
    IGeneric,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import {
    AttributeSwatch,
    AttributeValue,
    CommerceProperty,
    ProductDimension,
    ProductSearchResult,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    convertDimensionTypeToProductDimensionType,
    getDimensionsForSelectedVariant,
    GetDimensionsForSelectedVariantInput,
    getProductAvailabilitiesForSelectedVariant,
    ProductAvailabilitiesForSelectedVariantInput,
    IProductInventoryInformation,
    getSelectedVariant,
    SelectedVariantInput,
    IDimensionForSelectedVariant,
    Dictionary,
    DimensionTypes,
    generateImageUrl,
    getProductPageUrlSync,
    IDimensionsApp,
    StringExtensions
    //validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    Node
    //onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';

export interface IProductComponentProps extends IComponentProps<{ product?: ProductSearchResult }> {
    className?: string;
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    ratingAriaLabel?: string;
    ratingCountAriaLabel?: string;
    allowBack?: boolean;
    telemetryContent?: ITelemetryContent;
    quickViewButton?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    isEnabledProductDescription?: boolean;
    isPriceMinMaxEnabled?: boolean;
    priceResources?: IPriceComponentResources;
    inventoryLabel?: string;
    dimensionAvailabilities?: IProductsDimensionsAvailabilities[];
    swatchItemAriaLabel?: string;
}

export interface IListAddToCartViewProps {
    ContainerProps: INodeProps;
    button?: React.ReactNode;
}

export interface ProductConversionMatrix {
    PRODUCT: number;
    FACTOR: number;
    FromUnit: string;
    ToUnit: string;
}

export interface IProductComponent extends IComponent<IProductComponentProps> {}

const PriceComponentActions = {};

/**
 * Gets the product page url from the default swatch selected.
 * @param  productData - Product card to be rendered.
 * @returns The default color swatch selected if any.
 */
function getDefaultSwatchSelected(coreContext: ICoreContext, productData?: ProductSearchResult): AttributeSwatch | null {
    if (!productData || !productData.AttributeValues) {
        return null;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const attributeSwatches = productData.AttributeValues.find(
        attributeValue => attributeValue.KeyName?.toLocaleLowerCase() === dimensionToPreSelectInProductCard
    )?.Swatches;

    if (!ArrayExtensions.hasElements(attributeSwatches)) {
        return null;
    }

    const defaultSwatch = attributeSwatches.find(item => item.IsDefault === true) ?? attributeSwatches[0];
    return defaultSwatch;
}

/**
 * Gets the product image from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductImageUrlFromDefaultSwatch(coreContext: ICoreContext, productData?: ProductSearchResult): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    const swatchProductImageUrls = defaultSwatch?.ProductImageUrls;
    if (!ArrayExtensions.hasElements(swatchProductImageUrls)) {
        return productData?.PrimaryImageUrl;
    }

    return generateImageUrl(swatchProductImageUrls[0], coreContext.request.apiSettings);
}

/**
 * Updates the product url link to product details page.
 * @param  productDetailsPageUrl - Product page url.
 * @param  coreContext - Context of the module using the component.
 * @param  queryString - Querystring to be added to the URL.
 * @returns The update product page url.
 */
function updateProductUrl(productDetailsPageUrl: string, coreContext: ICoreContext, queryString: string): string {
    const sourceUrl = new URL(productDetailsPageUrl, coreContext.request.apiSettings.baseUrl);
    if (sourceUrl.search) {
        sourceUrl.search += `&${queryString}`;
    } else {
        sourceUrl.search += queryString;
    }

    const updatedUrl = new URL(sourceUrl.href);
    return updatedUrl.pathname + sourceUrl.search;
}

/**
 * Gets the react node for product unit of measure display.
 * @param  unitOfMeasure - DefaultUnitOfMeasure property from product.
 * @returns The node representing markup for unit of measure component.
 */
// function renderProductUnitOfMeasure(unitOfMeasure?: string): JSX.Element | null {
//     if (!unitOfMeasure) {
//         return null;
//     }
//     return (
//         <div className='msc-product__unit-of-measure'>
//             <span>{unitOfMeasure}</span>
//         </div>
//     );
// }

/**
 * Gets the react node for product availability.
 * @param inventoryAvailabilityLabel - The product information.
 * @returns The node representing markup for product availability.
 */
// function renderProductAvailability(inventoryAvailabilityLabel: string | undefined): JSX.Element | null {
//     if (!inventoryAvailabilityLabel || inventoryAvailabilityLabel === '') {
//         return null;
//     }

//     return (
//         <div className='msc-product__availability'>
//             <span>{inventoryAvailabilityLabel}</span>
//         </div>
//     );
// }

/**
 * Renders product comparison button similar to the quick view button.
 * @param productComparisonButton - React element of the button.
 * @param product - Current product info.
 * @param catalogId - Current catalog.
 * @returns React element for the specific product.
 */
// function renderProductComparisonButton(
//     productComparisonButton: React.ReactNode,
//     product: ProductSearchResult,
//     catalogId: number
// ): JSX.Element | undefined {
//     validateCatalogId(catalogId);
//     return React.cloneElement(productComparisonButton as React.ReactElement, { product, catalogId });
// }

/**
 * Gets the product page url from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param productUrl - Product page url for the product card.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductPageUrlFromDefaultSwatch(
    coreContext: ICoreContext,
    productUrl: string,
    productData?: ProductSearchResult
): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    if (!defaultSwatch?.SwatchValue) {
        return productUrl;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const queryStringEncoded = encodeURIComponent(defaultSwatch.SwatchValue);
    const queryString = `${dimensionToPreSelectInProductCard}=${queryStringEncoded}`;
    return updateProductUrl(productUrl, coreContext, queryString);
}

const ProductCard: React.FC<IProductComponentProps> = ({
    data,
    context,
    imageSettings,
    savingsText,
    freePriceText,
    originalPriceText,
    currentPriceText,
    ratingAriaLabel,
    ratingCountAriaLabel,
    allowBack,
    typeName,
    id,
    telemetryContent,
    quickViewButton,
    productComparisonButton,
    isPriceMinMaxEnabled,
    priceResources,
    inventoryLabel,
    dimensionAvailabilities,
    swatchItemAriaLabel
}) => {
    const product = data.product;
    const [quantity, setQuantity] = useState(0);
    const [maxQuantity, setMaxQuantity] = useState(0);
    let productUrl = getProductPageUrlSync(product?.Name ?? '', product?.RecordId ?? Number.MIN_VALUE, context.actionContext, undefined);
    if (allowBack) {
        productUrl = updateProductUrl(productUrl, context, 'back=true');
    }
    const productImageUrlFromSwatch = getProductImageUrlFromDefaultSwatch(context, product) ?? product?.PrimaryImageUrl;
    const productPageUrlFromSwatch = getProductPageUrlFromDefaultSwatch(context, productUrl, product) ?? productUrl;
    const [productPageUrl, setProductPageUrl] = useState<string>(productPageUrlFromSwatch);
    const [productImageUrl, setProductImageUrl] = useState<string | undefined>(productImageUrlFromSwatch);
    React.useEffect(() => {
        setProductPageUrl(productPageUrlFromSwatch);
        setProductImageUrl(productImageUrlFromSwatch);
    }, [productUrl, productPageUrlFromSwatch, productImageUrlFromSwatch]);
    const [selectedSwatchItems] = useState(new Dictionary<DimensionTypes, ISwatchItem>());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    const enableStockCheck = context.app.config.enableStockCheck;
    const siteContext = context as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
    const [availableDimensions, setAvailableDimensions] = useState<string[]>([]);
    const [dimensionValues, setDimensionValues] = useState<ProductDimension[]>([]);
    const [prodDimensions, setProdDimensions] = useState<IDimensionForSelectedVariant[]>([]);
    const [defaultDimensions, setDefaultDimensions] = useState<IDimensionForSelectedVariant[]>([]);
    const [productVariant, setProductVariant] = useState<number>(0);
    const [cartProduct, setCartProduct] = useState<SimpleProduct>();
    const [productAvailableQuantity, setProductAvailableQuantity] = useState<IProductInventoryInformation>();
    //get available dimensions for product
    const isMobile = context.request.device.Type === 'Mobile';

    React.useEffect(() => {
        if (data.product && data.product.AttributeValues) {
            let dimensions: string[] = [];
            data.product.AttributeValues.forEach(item => {
                const dimensionTypeValue = item.KeyName?.toLocaleLowerCase() ?? '';
                if (shouldDisplayDimension(dimensionTypeValue)) {
                    dimensions.push(item.KeyName!);
                }
            });
            setAvailableDimensions(dimensions);
        }
    }, [data.product!.AttributeValues]);

    React.useEffect(() => {
        async function getSelectedVariantbyDimensions() {
            let variant = await getSelectedVariant(
                new SelectedVariantInput(
                    data.product!.MasterProductId ? data.product?.MasterProductId! : data.product!.RecordId!,
                    context.request.apiSettings.channelId,
                    dimensionValues,
                    undefined,
                    context.request
                ),
                context.actionContext
            );

            if (variant?.RecordId != data.product?.MasterProductId) {
                const newAvailableQuantity = await getProductAvailabilitiesForSelectedVariant(
                    new ProductAvailabilitiesForSelectedVariantInput(variant?.RecordId!, context.request.apiSettings.channelId),
                    context.actionContext
                );

                if (newAvailableQuantity && newAvailableQuantity.length > 0) {
                    setProductAvailableQuantity(newAvailableQuantity[0]);
                    setMaxQuantity(newAvailableQuantity[0].ProductAvailableQuantity.AvailableQuantity || 0);
                } else {
                    setProductAvailableQuantity(undefined);
                }
                setProductVariant(variant?.RecordId!);
                setCartProduct({
                    RecordId: variant?.RecordId!,
                    ItemId: data.product?.ItemId,
                    Name: data.product?.Name,
                    Description: data.product?.Description,
                    ProductTypeValue: data.product?.RecordId!,
                    DefaultUnitOfMeasure: data.product?.DefaultUnitOfMeasure,
                    BasePrice: data.product?.BasePrice!,
                    Price: data.product?.Price!,
                    AdjustedPrice: data.product?.Price!,
                    MasterProductId: data.product?.MasterProductId,
                    PrimaryImageUrl: data.product?.PrimaryImageUrl,
                    ExtensionProperties: variant?.ExtensionProperties
                });
            } else {
                setProductVariant(0);
                setCartProduct(undefined);
            }
        }
        setQuantity(0);
        if (availableDimensions.length > 0 && availableDimensions.length == dimensionValues.length) {
            getSelectedVariantbyDimensions();
        } else {
            setProductVariant(0);
            setCartProduct(undefined);
        }
    }, [dimensionValues]);

    React.useEffect(() => {
        async function getDefaultDimensionsForVariant() {
            let prodDimensions = await getDimensionsForSelectedVariant(
                new GetDimensionsForSelectedVariantInput(
                    data.product?.MasterProductId ?? 0,
                    context.request.apiSettings.channelId,
                    [],
                    context.request
                ),
                context.actionContext
            );
            prodDimensions = prodDimensions.sort((a, b) => {
                return b.DimensionTypeValue - a.DimensionTypeValue;
            });
            setDefaultDimensions(prodDimensions);
        }

        getDefaultDimensionsForVariant();
    }, []);

    React.useEffect(() => {
        async function getProductDimensionsForVariant() {
            const mappedDimensions: ProductDimension[] = [];
            defaultDimensions.forEach(dimension => {
                var selectedDimension = dimensionValues.find(dv => dv.DimensionTypeValue == dimension.DimensionTypeValue);
                if (selectedDimension) {
                    dimension.DimensionValues?.forEach(ddv => {
                        if (ddv.RecordId === selectedDimension?.DimensionValue?.RecordId) {
                            mappedDimensions.push({
                                DimensionTypeValue: dimension.DimensionTypeValue,
                                DimensionValue: ddv,
                                ExtensionProperties: dimension.ExtensionProperties
                            });
                        }
                    });
                }
            });

            const prodDimensions = await getDimensionsForSelectedVariant(
                new GetDimensionsForSelectedVariantInput(
                    data.product?.MasterProductId ?? 0,
                    context.request.apiSettings.channelId,
                    mappedDimensions,
                    context.request
                ),
                context.actionContext
            );
            setProdDimensions(prodDimensions);
        }

        // You need to restrict it at some point
        // This is just dummy code and should be replaced by actual
        if (dimensionValues.length > 0) {
            getProductDimensionsForVariant();
        }
    }, [dimensionValues]);

    /**
     * Updates the product page and Image url based on swatch selected.
     * @param coreContext - Context of the caller.
     * @param swatchItem - Dimension swatch selected
     */
    const updatePageAndImageUrl = React.useCallback(
        (coreContext: ICoreContext, swatchItem: ISwatchItem) => {
            const dimensionType = swatchItem.dimensionType;
            selectedSwatchItems.setValue(dimensionType, swatchItem);
            if (StringExtensions.isNullOrWhitespace(swatchItem.value)) {
                return;
            }
            const queryString = `${dimensionType}=${swatchItem.value}`;
            let productPageUrlWithSwatch = '';
            if (productPageUrl.includes(dimensionType)) {
                const newUrl = new URL(productPageUrl, coreContext.request.apiSettings.baseUrl);
                newUrl.searchParams.delete(dimensionType);
                productPageUrlWithSwatch = updateProductUrl(newUrl.toString(), context, queryString);
            } else {
                productPageUrlWithSwatch = updateProductUrl(productPageUrl, context, queryString);
            }
            setProductPageUrl(productPageUrlWithSwatch);
            if (dimensionType === dimensionToPreSelectInProductCard) {
                const swatchProductImageUrl = ArrayExtensions.hasElements(swatchItem.productImageUrls)
                    ? swatchItem.productImageUrls[0]
                    : undefined;
                const newImageUrl = generateImageUrl(swatchProductImageUrl, coreContext.request.apiSettings);
                setProductImageUrl(newImageUrl);
            }
        },
        [selectedSwatchItems, context, productPageUrl]
    );

    if (!product) {
        return null;
    }

    /**
     * Checks if rendering the particular dimensions is allowed for product card.
     * @param dimensionType - Dimension to be displayed.
     * @returns Updates the state with new product page url.
     */
    function shouldDisplayDimension(dimensionType: string): boolean {
        const dimensionsContext = context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = dimensionsContext.app.config.dimensionsInProductCard;
        return (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            dimensionsToDisplayOnProductCard.includes(dimensionType.toLocaleLowerCase() as DimensionTypes)
        );
    }

    const handleDimensionChange = (
        dataTypeValue: number | undefined,
        selectedValue: string,
        recordId: number,
        ExtensionProperties: CommerceProperty[] | undefined
    ) => {
        const selectedDimensionValues = defaultDimensions.find(x => {
            return x.DimensionTypeValue === dataTypeValue;
        });
        selectedValue = selectedDimensionValues?.DimensionValues!.find(x => {
            return x.RecordId === recordId;
        })?.Value!;
        setDimensionValues(previousValue => {
            if (selectedValue == '0' && previousValue.some(obj => obj.DimensionTypeValue === dataTypeValue)) {
                var recordIndex = previousValue.findIndex(item => item.DimensionTypeValue == dataTypeValue);
                previousValue.splice(recordIndex, 1);
                return previousValue;
            }
            const updatedValue = previousValue.map(obj => {
                if (obj.DimensionTypeValue == dataTypeValue) {
                    return {
                        ...obj,
                        DimensionValue: {
                            Value: selectedValue,
                            RecordId: recordId
                        }
                    };
                }
                return obj;
            });

            if (!previousValue.some(obj => obj.DimensionTypeValue === dataTypeValue)) {
                updatedValue.push({
                    DimensionTypeValue: dataTypeValue ?? 0,
                    DimensionValue: {
                        Value: selectedValue,
                        RecordId: recordId
                    },
                    ExtensionProperties: ExtensionProperties
                });
            }

            return updatedValue;
        });
    };

    function renderDimensions() {
        //let dimensionIndex = 0;
        return (
            <div className='msc-product__dimensions'>
                {defaultDimensions.map(dimension => {
                    if (prodDimensions) {
                        prodDimensions.forEach(pd => {
                            if (pd.DimensionTypeValue == dimension.DimensionTypeValue) {
                                dimension = pd;
                            }
                        });
                    }
                    const dimensionTypeValue = dimension.DimensionTypeValue == 1 ? 'length' : 'width';
                    let sortedList = dimension.dimensionValuesWithInventory!.sort((a, b) => {
                        return parseFloat(a.DimensionValue?.Value!) - parseFloat(b.DimensionValue?.Value!);
                    });
                    return (
                        <>
                            <select
                                onChange={e =>
                                    handleDimensionChange(
                                        dimension.DimensionTypeValue,
                                        e.target.selectedOptions[0].text,
                                        Number(e.target.value),
                                        dimension.ExtensionProperties
                                    )
                                }
                            >
                                <option value={'0'}>{dimensionTypeValue}</option>
                                {sortedList.map(dv => {
                                    if (dimensionValues) {
                                        let selectedDimension = dimensionValues.find(
                                            d => d.DimensionTypeValue == dimension.DimensionTypeValue
                                        );
                                        if (selectedDimension?.DimensionValue?.RecordId == dv.DimensionValue!.RecordId) {
                                            return (
                                                <option selected value={dv.DimensionValue!.RecordId!}>
                                                    {parseFloat(dv.DimensionValue!.Value!).toString()}
                                                </option>
                                            );
                                        }
                                    }

                                    return dv.PhysicalAvailableInventoryLevelLabel == 'Available' ? (
                                        <option value={dv.DimensionValue!.RecordId!}>
                                            {parseFloat(dv.DimensionValue!.Value!).toString()}
                                            {' - In Stock'}
                                        </option>
                                    ) : (
                                        <option disabled value={dv.DimensionValue!.RecordId!}>
                                            {parseFloat(dv.DimensionValue!.Value!).toString()}
                                            {' - Call to Order'}
                                        </option>
                                    );
                                })}
                            </select>
                        </>
                    );
                })}
            </div>
        );
    }

    /**
     * Gets the react node for product dimension as swatch.
     * @param  attributeValues - Attribute value property from product.
     * @returns The node representing markup for unit of measure component.
     */
    function renderProductDimensions(attributeValues?: AttributeValue[]): JSX.Element | null {
        if (!attributeValues) {
            return null;
        }

        return (
            <div className='msc-product__dimensions'>
                {attributeValues.map((item: AttributeValue) => {
                    const dimensionTypeValue = item.KeyName?.toLocaleLowerCase() ?? '';
                    if (!shouldDisplayDimension(dimensionTypeValue)) {
                        return null;
                    }
                    const siteContext = context as ICoreContext<IDimensionsApp>;
                    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
                    const dimensionType = dimensionTypeValue as DimensionTypes;

                    const swatches =
                        item.Swatches?.map<ISwatchItem>(swatchItem => {
                            return {
                                itemId: `${item.RecordId ?? ''}-${dimensionTypeValue}-${swatchItem.SwatchValue ?? ''}`,
                                value: swatchItem.SwatchValue ?? '',
                                dimensionType,
                                colorHexCode: swatchItem.SwatchColorHexCode,
                                imageUrl: swatchItem.SwatchImageUrl,
                                productImageUrls: swatchItem.ProductImageUrls,
                                isDefault: swatchItem.IsDefault,
                                swatchItemAriaLabel: swatchItemAriaLabel ? format(swatchItemAriaLabel, dimensionType) : '',
                                isDisabled:
                                    enableStockCheck &&
                                    dimensionAvailabilities?.find(
                                        dimensionAvailability => dimensionAvailability.value === (swatchItem.SwatchValue ?? '')
                                    )?.isDisabled
                            };
                        }) ?? [];
                    if (
                        dimensionType === dimensionToPreSelectInProductCard &&
                        ArrayExtensions.hasElements(swatches) &&
                        !swatches.some(swatch => swatch.isDefault)
                    ) {
                        swatches[0].isDefault = true;
                    }
                    return (
                        <ProductComponentSwatchComponent
                            context={context}
                            swatches={swatches}
                            onSelectDimension={updatePageAndImageUrl}
                            key={item.RecordId}
                        />
                    );
                })}
            </div>
        );
    }

    function renderQuickView(quickview: React.ReactNode, item?: number): JSX.Element | undefined {
        if (quickview === null) {
            return undefined;
        }
        const selectedDimensions: ProductDimension[] = selectedSwatchItems.getValues().map<ProductDimension>(swatches => {
            return {
                DimensionTypeValue: convertDimensionTypeToProductDimensionType(swatches.dimensionType),
                DimensionValue: {
                    RecordId: 0,
                    Value: swatches.value
                }
            };
        });
        return React.cloneElement(quickview as React.ReactElement, { selectedProductId: item, selectedDimensions });
    }

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- -- Do not need type check for appsettings
    //const isUnitOfMeasureEnabled = context.app.config && context.app.config.unitOfMeasureDisplayType === 'buyboxAndBrowse';

    function renderQuantity(arg0: number): JSX.Element | null | undefined {
        const qtyIncrement = () => {
            if (quantity < maxQuantity) {
                setQuantity(prevCount => prevCount + 1);
            }
        };
        const qtyDecrement = () => {
            if (quantity >= 1) {
                setQuantity(prevCount => prevCount - 1);
            }
        };

        const checkQuantity = (qtyText: string) => {
            let qtyValue = parseInt(qtyText);
            if (qtyValue > maxQuantity) {
                setQuantity(maxQuantity);
            } else {
                setQuantity(qtyValue);
            }
        };
        return (
            <div
                className='msc_quantity'
                title={
                    cartProduct
                        ? productAvailableQuantity && productAvailableQuantity!.IsProductAvailable
                            ? 'Choose quantity'
                            : 'Out of stock'
                        : 'Choose a variant'
                }
            >
                <button onClick={qtyDecrement} disabled={!!!productAvailableQuantity}>
                    <span>-</span>
                </button>

                <input
                    type='text'
                    className='msc_quantity_text_box'
                    onChange={e => checkQuantity(e.target.value)}
                    disabled={!!!productAvailableQuantity}
                    value={quantity > 0 ? quantity.toString() : ''}
                />
                <button onClick={qtyIncrement} disabled={!!!productAvailableQuantity}>
                    <span>+</span>
                </button>
            </div>
        );
    }

    // function truncateStr(text: string, lenthTobeShown: number): string {
    //     if (text.length < lenthTobeShown) {
    //         return text;
    //     } else {
    //         return text.substring(0, lenthTobeShown) + '...';
    //     }
    // }

    function getProductWeight(product: SimpleProduct | undefined, gridProduct: ProductSearchResult): string {
        let weightUom: string = '';
        if (gridProduct) {
            if (gridProduct.ExtensionProperties) {
                var extension = gridProduct.ExtensionProperties.find(ext => {
                    return ext.Key === 'CSSCOMMERCEWEIGHTUOM';
                });
                if (extension && extension?.Value?.StringValue !== '') {
                    weightUom = extension?.Value?.StringValue || '';
                }
            }
        }
        weightUom = weightUom ? weightUom : 'lb';
        let conversionValues: ProductConversionMatrix[] = [];
        if (product) {
            if (product.ExtensionProperties) {
                var extension = product.ExtensionProperties.find(ext => {
                    return ext.Key === 'CONVERSIONMATRIX';
                });
                if (extension && extension?.Value?.StringValue !== '') {
                    conversionValues = JSON.parse(extension?.Value?.StringValue || '');
                }
            }
        }
        if (conversionValues.length > 0) {
            var productUnit = gridProduct.DefaultUnitOfMeasure;
            var conversionValue = conversionValues.find(cv => {
                return cv.FromUnit === productUnit && cv.ToUnit === weightUom;
            });
            if (conversionValue) {
                var weightValue = quantity > 1 ? quantity * conversionValue.FACTOR : conversionValue.FACTOR;
                return weightValue.toFixed(2).toString();
            }
        }
        return '';
    }

    function getProductLFSF(product: SimpleProduct | undefined, gridProduct: ProductSearchResult): string {
        let lfUom: string = '';
        let sfUom: string = '';
        if (gridProduct) {
            if (gridProduct.ExtensionProperties) {
                var extension = gridProduct.ExtensionProperties.find(ext => {
                    return ext.Key === 'CSSCOMMERCELFUOM';
                });
                if (extension && extension?.Value?.StringValue !== '') {
                    lfUom = extension?.Value?.StringValue || '';
                }

                var extension2 = gridProduct.ExtensionProperties.find(ext => {
                    return ext.Key === 'CSSCOMMERCESFUOM';
                });
                if (extension2 && extension2?.Value?.StringValue !== '') {
                    sfUom = extension2?.Value?.StringValue || '';
                }
            }
        }
        let lengthUom = lfUom || sfUom;
        if (lengthUom === '') {
            lfUom = lfUom || 'ft';
            sfUom = sfUom || 'sqft';
            lengthUom = lfUom || sfUom;
        }
        let conversionValues: ProductConversionMatrix[] = [];
        if (product) {
            if (product.ExtensionProperties) {
                var extension = product.ExtensionProperties.find(ext => {
                    return ext.Key === 'CONVERSIONMATRIX';
                });
                if (extension && extension?.Value?.StringValue !== '') {
                    conversionValues = JSON.parse(extension?.Value?.StringValue || '');
                }
            }
        }
        if (conversionValues.length > 0) {
            var productUnit = gridProduct.DefaultUnitOfMeasure;
            var conversionValue = conversionValues.find(cv => {
                if (cv.FromUnit === productUnit && lengthUom == 'sqft' && cv.ToUnit === 'sqin') {
                    return true;
                }
                return cv.FromUnit === productUnit && cv.ToUnit === lengthUom;
            });
            if (conversionValue) {
                var weightValue = quantity > 1 ? quantity * conversionValue.FACTOR : conversionValue.FACTOR;
                if (conversionValue.ToUnit === 'sqin') {
                    weightValue = weightValue * 0.00694444; //1 sqin = 0.00694444  sqft
                }
                return weightValue.toFixed(2).toString();
            }
        }
        return '';
    }

    return isMobile ? (
        <>
            <div
                aria-label={renderLabel(
                    product.Name,
                    context.cultureFormatter.formatCurrency(product.Price),
                    product.AverageRating,
                    ratingAriaLabel
                )}
                className='msc-product'
                {...attribute}
            >
                <div className='msc-product-Name'>
                    <div className='msc-product__image'>
                        {renderProductPlacementImage(
                            imageSettings,
                            context.request.gridSettings,
                            productImageUrl,
                            product.PrimaryImageUrl,
                            product.Name,
                            context
                        )}
                    </div>
                    <div className='msc-product__details'>
                        <a href={productPageUrl}>
                            <span className='msc-product__title' title={product.Name}>
                                {product.Name}
                            </span>
                        </a>
                    </div>
                </div>
                {defaultDimensions && renderDimensions()}
                {renderQuantity(0)}
                <div className='msc-product-cart'>
                    <div className='msc-product-variant-info'>
                        {cartProduct && <div className='product_weight'>Weight: {getProductWeight(cartProduct, product)}</div>}
                        <br />
                        {cartProduct && <div className='product_lfsf'>LF/SF: {getProductLFSF(cartProduct, product)}</div>}
                    </div>
                    {cartProduct && quantity > 0 ? (
                        <Node className='mobile-cart' key={quantity}>
                            <AddToCartComponent
                                addToCartText='Add'
                                outOfStockText='Add'
                                quantity={quantity}
                                productAvailability={{
                                    AvailableQuantity: maxQuantity
                                }}
                                data={{ product: cartProduct }}
                                context={context}
                                id={id}
                                typeName={typeName}
                            />
                        </Node>
                    ) : (
                        <div className='mobile-cart'>
                            <button className='msc-add-to-cart ' disabled title='Add to cart'>
                                Add
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    ) : (
        <>
            <div
                aria-label={renderLabel(
                    product.Name,
                    context.cultureFormatter.formatCurrency(product.Price),
                    product.AverageRating,
                    ratingAriaLabel
                )}
                className='msc-product'
                {...attribute}
            >
                <div className='msc-product__image'>
                    {renderProductPlacementImage(
                        imageSettings,
                        context.request.gridSettings,
                        productImageUrl,
                        product.PrimaryImageUrl,
                        product.Name,
                        context
                    )}
                </div>

                <div style={{ display: 'none' }}>
                    {productVariant && (
                        <span>
                            Variant:{productVariant} , Product:{product.MasterProductId}
                        </span>
                    )}
                    {renderProductDimensions(product!.AttributeValues)}
                </div>
                <div className='msc-product__details'>
                    <div className='msc-product__Name'>
                        <div className='product_attributes'>
                            {product && renderExtensionProperty(product, 'CSSCOMMERCESIZEDIMNAME') && (
                                <h5 className='product_size_span'>
                                    {`Size Dimensions: ${renderExtensionProperty(product, 'CSSCOMMERCESIZEDIMNAME')}`}
                                </h5>
                            )}
                            {product && renderExtensionProperty(product, 'PRODUCTNOMINALDESIGNATION') && (
                                <h5 className='product_designation'>
                                    {`Nominal Designation: ${renderExtensionProperty(product, 'PRODUCTNOMINALDESIGNATION')}`}
                                </h5>
                            )}
                            <a href={productPageUrl}>
                                <h5
                                    className='msc-product__title'
                                    title={renderExtensionProperty(product, 'CSSCOMMERCEPRODUCTNAME') || product.Name}
                                >
                                    {renderExtensionProperty(product, 'CSSCOMMERCEPRODUCTNAME') || product.Name}
                                </h5>
                            </a>
                            {product && renderExtensionProperty(product, 'PRODUCTFEATURE') && (
                                <span className='product_features'>
                                    {`Features: ${renderExtensionProperty(product, 'PRODUCTFEATURE')}`}
                                </span>
                            )}
                        </div>
                    </div>

                    {defaultDimensions && renderDimensions()}
                </div>
                <div className='product-variant__attributes'>
                    {renderQuantity(0)}
                    {cartProduct && <span className='product_weight'>{`Weight: ${getProductWeight(cartProduct, product)}`}</span>}
                    {cartProduct && <span className='product_lfsf'>{`LF/SF: ${getProductLFSF(cartProduct, product)}`}</span>}
                </div>
                {quickViewButton && renderQuickView(quickViewButton, product.RecordId)}
                {cartProduct && quantity > 0 ? (
                    <Node className='ms-search-result-container__add-to-cart-container' key={quantity}>
                        {/* <button
                            className='msc-view-details'
                            onClick={e => {
                                onTelemetryClick(telemetryContent!, payLoad, product.Name!);
                                window.location.href = productPageUrl;
                            }}
                        >
                            Save
                        </button> */}
                        <AddToCartComponent
                            addToCartText='Add'
                            outOfStockText='Add'
                            quantity={quantity}
                            productAvailability={{
                                AvailableQuantity: productAvailableQuantity?.ProductAvailableQuantity.AvailableQuantity || 0
                            }}
                            data={{ product: cartProduct }}
                            context={context}
                            id={id}
                            typeName={typeName}
                        />
                    </Node>
                ) : (
                    <div className='ms-search-result-container__add-to-cart-container'>
                        {/* <button
                            className='msc-view-details'
                            onClick={e => {
                                onTelemetryClick(telemetryContent!, payLoad, product.Name!);
                                window.location.href = productPageUrl;
                            }}
                        >
                            Save
                        </button> */}
                        <button className='msc-add-to-cart ' disabled title='Add to cart'>
                            Add
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

function renderLabel(
    name?: string,
    price?: string,
    rating?: number,
    ratingAriaLabelText?: string,
    reviewCount?: number,
    ratingCountAriaLabelText?: string
): string {
    const reviewCountArialableText = getReviewAriaLabel(reviewCount, ratingCountAriaLabelText ?? '');
    return `${name ?? ''} ${price ?? ''} ${getRatingAriaLabel(rating, ratingAriaLabelText)}${
        reviewCountArialableText ? ` ${reviewCountArialableText}` : ''
    }`;
}

function renderExtensionProperty(product: ProductSearchResult | undefined, propertyName: string): string {
    let propertyValue: string = '';
    if (product) {
        if (product.ExtensionProperties) {
            var extension = product.ExtensionProperties.find(ext => {
                return ext.Key === propertyName;
            });
            if (extension && extension?.Value?.StringValue !== '') {
                propertyValue = extension?.Value?.StringValue || '';
            }
        }
    }
    return propertyValue;
}

function getRatingAriaLabel(rating?: number, ratingAriaLabel?: string): string {
    if (rating && ratingAriaLabel) {
        const roundedRating = rating.toFixed(2);
        return format(ratingAriaLabel || '', roundedRating, '5');
    }
    return '';
}

function getReviewAriaLabel(reviewCount?: number, ratingCountAriaLabelText?: string): string {
    if (reviewCount && ratingCountAriaLabelText) {
        return format(ratingCountAriaLabelText || '', reviewCount);
    }
    return '';
}

function renderProductPlacementImage(
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    context?: ICoreContext<IGeneric<IAny>>
): JSX.Element | null {
    if (!imageUrl || !gridSettings || !imageSettings) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Site level config can be of any type.
    const contextConfig = context?.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = fallbackImageUrl;
    if (emptyPlaceHolderImage && fallbackImageUrl) {
        fallbackImageSource = `${fallbackImageUrl},${emptyPlaceHolderImage}`;
    }
    const img: IImageData = {
        src: imageUrl,
        altText: altText ? altText : '',
        fallBackSrc: fallbackImageSource
    };
    const imageProps = {
        gridSettings,
        imageSettings
    };
    imageProps.imageSettings.cropFocalRegion = true;
    return (
        <Image
            requestContext={context?.actionContext.requestContext}
            {...img}
            {...imageProps}
            loadFailureBehavior='empty'
            bypassHideOnFailure
        />
    );
}

export const ProductComponent: React.FunctionComponent<IProductComponentProps> = msdyn365Commerce.createComponentOverride<
    IProductComponent
>('Product', { component: ProductCard, ...PriceComponentActions });

export default ProductComponent;
